<template>
    <!-- NOTE: Styling has been added for MOEA Admin messages. Instead of --external, add --admin to .message class. Can't see any fields that distinguish user levels in data -->

    <div
        v-if="recipient.display"
        class="new-message-thread"
    >
        <!-- Page title(s) -->
        <h1 class="page-title">
            New Message Thread
        </h1>
        <h2 class="h3 mb-5 font-weight-normal title-recipient">
            <span class="color-mid-grey font-weight-bold">To:</span> {{ recipient.display }}
        </h2>

        <!-- New message -->
        <form
            id="message-form"
            class="mt-5"
            @submit.prevent="handleCreateThread"
        >
            <!-- Field: Subject -->
            <div class="field-wrap">
                <h4 class="mb-3 color-mid-grey text-bold">
                    Subject
                </h4>
                <label
                    for="thread-subject"
                    class="sr-only"
                >
                    Subject
                </label>
                <div class="field-wrap__input mb-4">
                    <input
                        id="thread-subject"
                        v-model="subject"
                        v-validate="{ required: true }"
                        :class="{ 'has-error': showErrors && errors.has('thread-subject') }"
                        type="text"
                        name="thread-subject"
                        placeholder="Add message subject here..."
                    >
                </div>
                <div
                    v-if="showErrors && errors.has('thread-subject')"
                    class="py-3"
                >
                    <div class="d-inline-block alert-error p-3 rounded-corners">
                        {{ errors.first('thread-subject') }}
                    </div>
                </div>
            </div>
            <!-- END Field: Subject -->
            <div class="field-wrap">
                <h4 class="mb-3 color-mid-grey text-bold">
                    Message
                </h4>
                <label
                    for="thread-message"
                    class="sr-only"
                >
                    Message
                </label>
                <div class="field-wrap__input field-wrap__input mb-4">
                    <textarea
                        id="thread-message"
                        v-model="message"
                        v-validate="{ required: true }"
                        :class="{ 'has-error': showErrors && errors.has('thread-message') }"
                        name="thread-message"
                        placeholder="Add message here..."
                        rows="10"
                    />
                </div>
                <div
                    v-if="showErrors && errors.has('thread-message')"
                    class="py-3"
                >
                    <div class="d-inline-block alert-error p-3 rounded-corners">
                        {{ errors.first('thread-message') }}
                    </div>
                </div>
            </div>
            <button
                type="submit"
                class="btn btn--secondary"
            >
                Send Message
            </button>
        </form>
        <!-- END New message -->
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import api from '../api';

export default {
    name: 'NewMessageThread',
    data() {
        return {
            subject: '',
            message: '',
            showErrors: false,
            offerId: 0
        };
    },
    computed: {
        ...mapState('user', ['user']),
        ...mapState('properties', ['property']),
        recipient() {
            const recipient = {
                toType: this.$route.params.toType,
                toId: this.$route.params.toId,
                support: this.$route.params.support,
                display: ''
            };

            switch (this.$route.params.toType) {
            case 'support':
                recipient.display = 'My Online Estate Agent Support';
                break;
            case 'property':
                recipient.display = this.property && this.property.full_address && `Owner of ${this.property.full_address}`;
                break;
            case 'offer':
                recipient.display = this.offer && `${this.offer.first_name} ${this.offer.last_name} (Offer #${this.offer.id} on ${this.offer.property.name})`;
                break;
            case 'viewing':
                recipient.display = this.viewing && `${this.viewing.user.full_name} (Viewing #${this.viewing.id} on ${this.viewing.property_name})`;
                break;
            default:
                break;
            }

            return recipient;
        },
        offer() {
            try {
                const offerId = parseInt(this.$route.params.toId, 10);
                return this.user.properties.find(property => property.offers.some(offer => offer.id === offerId))
                    .offers.find(offer => offer.id === offerId);
            }
            catch (e) {
                //
            }

            return null;
        },
        viewing() {
            try {
                const viewingId = parseInt(this.$route.params.toId, 10);
                return this.user.properties.find(property => property.events.some(event => event.type === 'viewing' && event.id === viewingId))
                    .events.find(event => event.id === viewingId);
            }
            catch (e) {
                //
            }

            return null;
        }
    },
    created() {
        if (['support', 'property', 'offer', 'viewing'].indexOf(this.$route.params.toType) !== -1) {
            if (this.$route.params.toType === 'property' && !(this.property && this.property.id !== parseInt(this.$route.params.to, 10))) {
                this.clearProperty().then(() => {
                    this.getProperty({ propertyId: this.$route.params.toId });
                });
            }
        }
        else {
            this.$router.push({ name: 'messages' });
        }
    },
    methods: {
        ...mapActions('properties', ['getProperty', 'clearProperty']),
        ...mapActions('messages', ['createThread']),
        handleCreateThread() {
            this.$validator.validateAll().then((result) => {
                if (result) {
                    this.createThread({
                        target_type: this.recipient.toType,
                        target_id: this.recipient.toId,
                        subject: this.subject,
                        message: this.message,
                        support: this.recipient.support
                    })
                        .then((response) => {
                            try {
                                if (response.data.payload.thread_id) {
                                    this.$router.push({ name: 'message', params: { threadId: response.data.payload.thread_id } });
                                }
                            }
                            catch (e) {
                                this.$router.push({ name: 'messages' });
                            }
                        })
                        .catch(() => {
                            this.$toasted.error('There was an error while attempting to send your message.');
                        });
                }
                else {
                    this.showErrors = true;
                    this.$toasted.error('Please complete the required fields');
                }
            });
        }
    }
};
</script>

<style lang="scss">
.new-message-thread {
    .title-recipient {
        max-width: 60rem;
    }
}
</style>
